import { useCallback, useEffect, useRef } from 'react'
import { useContextSelector } from 'use-context-selector'
import { getAppUserData } from 'app/@utils'
import { AppTourContext } from '../@context'
import { isTourCompleted, markTourCompleted, resetTourForUser } from '../@utils'

// getStepsFn() returns steps with `contentKey` (i18n key, namespace `tour`); AppTour resolves the text.
export default function useTour(tourKey, getStepsFn, { autoStart = true } = {}) {
    const updateState = useContextSelector(AppTourContext, ({ updateState }) => updateState)
    const isRunning = useContextSelector(AppTourContext, ({ state }) => state.isRunning)
    const hasAutoStarted = useRef(false)
    const wasRunning = useRef(false)

    const { userId } = getAppUserData()

    const filterSteps = useCallback(steps =>
        steps.filter(step => step.data?.skipFilter || document.querySelector(step.target))
    , [])

    const startTour = useCallback(() => {
        const rawSteps = getStepsFn()
        const validSteps = filterSteps(rawSteps)
        if (validSteps.length > 0) {
            updateState({ isRunning: true, stepIndex: 0, steps: validSteps, tourKey })
        }
    }, [getStepsFn, filterSteps, updateState, tourKey])

    const restartTour = useCallback(() => {
        resetTourForUser(userId, tourKey)
        startTour()
    }, [userId, tourKey, startTour])

    useEffect(() => {
        if (!autoStart) return
        if (hasAutoStarted.current) return
        if (!userId) return

        const timer = setTimeout(() => {
            if (!isTourCompleted(userId, tourKey)) {
                hasAutoStarted.current = true
                startTour()
            }
        }, 2000)

        return () => clearTimeout(timer)
    }, [autoStart, userId, tourKey, startTour])

    useEffect(() => {
        if (isRunning) {
            wasRunning.current = true
        }
        if (!isRunning && wasRunning.current) {
            wasRunning.current = false
            markTourCompleted(userId, tourKey)
        }
    }, [isRunning, userId, tourKey])

    return { startTour, restartTour, isRunning }
}
